import React, { useState } from 'react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { CallbackModal } from '../components/CallbackModal';
import { MapPin, Star, Scan, Languages, Calendar, MessageCircle, CheckCircle2, Plus, Minus, Sparkles, Smile, Palette } from 'lucide-react';
import { Link } from 'react-router-dom';
const BOOKING_URL = 'https://denticoclinic.patientportal.onl/bookings';
const WHATSAPP_URL = 'https://wa.me/447345197820';
const TELEGRAM_URL = 'https://t.me/dentico_clinic';
function TelegramIcon({
  className
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 24 24" fill="currentColor" className={className} data-id="element-3010">
      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" data-id="element-3011" />
    </svg>;
}
function AccordionItem({
  question,
  answer,
  isOpen,
  onToggle
}: {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return <div className="border-b border-gray-100 last:border-b-0" data-id="element-3012">
      <button onClick={onToggle} className="w-full flex items-center justify-between py-4 text-left focus:outline-none group" data-id="element-3013">
        <span className="text-lg font-medium text-gray-900 group-hover:text-teal-600 transition-colors" data-id="element-3014">
          {question}
        </span>
        <span className={`flex-shrink-0 ml-4 w-6 h-6 rounded-full flex items-center justify-center transition-colors ${isOpen ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-400'}`} data-id="element-3015">
          {isOpen ? <Minus className="w-4 h-4" data-id="element-3016" /> : <Plus className="w-4 h-4" data-id="element-3017" />}
        </span>
      </button>
      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-96 opacity-100 pb-4' : 'max-h-0 opacity-0'}`} data-id="element-3018">
        <p className="text-gray-600 leading-relaxed" data-id="element-3019">{answer}</p>
      </div>
    </div>;
}
export function VeneersLondonRuPage() {
  const [isCallbackOpen, setIsCallbackOpen] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };
  const faqs = [{
    q: 'Сколько стоят виниры в Лондоне?',
    a: 'Цена зависит от материала (керамика или композит), количества зубов и сложности работы. После консультации мы предлагаем детальный план лечения и смету.'
  }, {
    q: 'Нужно ли обтачивать зубы под виниры?',
    a: 'В большинстве случаев требуется минимальная подготовка эмали (0.3-0.5 мм), но объем зависит от клинической ситуации. Мы всегда выбираем максимально щадящий подход (minimally invasive).'
  }, {
    q: 'Сколько служат виниры?',
    a: 'Керамические виниры могут служить 10-15 лет и более при правильном уходе. Срок службы зависит от материала, прикуса и гигиены. На консультации мы объясняем, как продлить долговечность.'
  }, {
    q: 'Можно ли сделать естественный результат?',
    a: 'Да, это наша специализация. Мы подбираем форму, текстуру и цвет индивидуально, чтобы улыбка выглядела гармонично и натурально, а не искусственно.'
  }, {
    q: 'Какая разница между фарфоровыми и композитными винирами?',
    a: 'Фарфоровые (керамические) виниры прочнее, долговечнее и устойчивее к окрашиванию. Композитные виниры (bonding) доступнее по цене и часто устанавливаются за один визит, но требуют более частой полировки.'
  }, {
    q: 'Сколько визитов нужно для виниров?',
    a: 'Обычно 2-3 визита для керамических виниров: консультация, подготовка/оттиски, установка. Композитные виниры часто можно сделать за один визит.'
  }, {
    q: 'Можно ли получить консультацию на русском?',
    a: 'Да, в Dentico Clinic возможно общение на русском языке во время консультации и лечения.'
  }, {
    q: 'Где находится Dentico Clinic?',
    a: 'Angel / Islington, 360–364 City Road, London EC1V 2PY. 5 минут пешком от станции Angel.'
  }];
  // Schemas
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.a
      }
    }))
  };
  const procedureSchema = {
    '@context': 'https://schema.org',
    '@type': 'MedicalProcedure',
    name: 'Dental Veneers',
    description: 'Cosmetic dental procedure to improve the appearance of teeth using veneers.',
    procedureType: 'http://schema.org/NoninvasiveProcedure',
    provider: {
      '@type': 'Dentist',
      name: 'Dentico Clinic',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '360-364 City Road',
        addressLocality: 'London',
        postalCode: 'EC1V 2PY',
        addressCountry: 'GB'
      }
    }
  };
  return <Layout data-id="element-3020">
      <CallbackModal isOpen={isCallbackOpen} onClose={() => setIsCallbackOpen(false)} data-id="element-3021" />

      {/* Structured Data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(faqSchema)
    }} data-id="element-3022" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(procedureSchema)
    }} data-id="element-3023" />

      {/* 1. HERO SECTION */}
      <section className="relative bg-white overflow-hidden" data-id="element-3024">
        <div className="absolute inset-0 bg-teal-50/50 -skew-y-3 origin-top-left transform translate-y-20" data-id="element-3025" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-16 lg:pt-20 lg:pb-24 relative" data-id="element-3026">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-3027">
            <div className="space-y-6" data-id="element-3028">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-100 text-teal-800 text-sm font-medium" data-id="element-3029">
                <span className="relative flex h-2 w-2" data-id="element-3030">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-75" data-id="element-3031"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-teal-500" data-id="element-3032"></span>
                </span>
                Эстетическая стоматология
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight tracking-tight" data-id="element-3033">
                Виниры в Лондоне –{' '}
                <span className="text-teal-600" data-id="element-3034">Dentico Clinic</span>
                <br data-id="element-3035" />
                <span className="text-2xl sm:text-3xl font-medium text-gray-500 mt-2 block" data-id="element-3036">
                  (Angel, Islington)
                </span>
              </h1>

              <p className="text-lg text-gray-600 max-w-lg leading-relaxed" data-id="element-3037">
                Создайте идеальную улыбку с помощью керамических виниров или
                композитного бондинга. Естественный результат, минимальное
                вмешательство, консультация на русском.
              </p>

              {/* Trust Badges */}
              <div className="flex flex-wrap gap-3 text-sm font-medium text-gray-700" data-id="element-3038">
                <div className="flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-lg border border-gray-200 shadow-sm" data-id="element-3039">
                  <Star className="w-4 h-4 text-yellow-400 fill-current" data-id="element-3040" />
                  Smile Makeover
                </div>
                <div className="flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-lg border border-gray-200 shadow-sm" data-id="element-3041">
                  <MapPin className="w-4 h-4 text-teal-600" data-id="element-3042" />
                  Angel, EC1V
                </div>
                <div className="flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-lg border border-gray-200 shadow-sm" data-id="element-3043">
                  <Languages className="w-4 h-4 text-teal-600" data-id="element-3044" />
                  Русский язык
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-4" data-id="element-3045">
                <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-3046">
                  <Button size="lg" className="w-full gap-2 shadow-lg shadow-teal-600/20" data-id="element-3047">
                    <Calendar className="w-5 h-5" data-id="element-3048" />
                    Записаться на консультацию
                  </Button>
                </a>
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-3049">
                  <Button variant="outline" size="lg" className="w-full gap-2 border-teal-200 text-teal-700 hover:bg-teal-50" data-id="element-3050">
                    <MessageCircle className="w-5 h-5 text-green-600" data-id="element-3051" />
                    WhatsApp
                  </Button>
                </a>
              </div>
            </div>

            <div className="relative" data-id="element-3052">
              <div className="absolute inset-0 bg-teal-200 rounded-3xl transform rotate-3 blur-sm opacity-30" data-id="element-3053"></div>
              <img src="https://images.unsplash.com/photo-1579684385127-1ef15d508118?auto=format&fit=crop&w=1200&q=80" alt="Виниры и эстетическая стоматология - Dentico Clinic" className="relative rounded-3xl shadow-2xl object-cover h-[300px] sm:h-[400px] lg:h-[500px] w-full" data-id="element-3054" />
            </div>
          </div>
        </div>
      </section>

      {/* 2. WHAT ARE VENEERS */}
      <section className="py-16 bg-white" data-id="element-3055">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3056">
          <div className="text-center mb-10" data-id="element-3057">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-3058">
              Что такое виниры?
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed" data-id="element-3059">
              Виниры — это ультратонкие керамические накладки, которые
              фиксируются на переднюю поверхность зубов для улучшения их формы,
              цвета и положения. Это идеальное решение для коррекции сколов,
              промежутков между зубами (диастем), изменения цвета эмали, который
              не поддается отбеливанию, или выравнивания незначительных
              неровностей.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed mt-4" data-id="element-3060">
              Современные виниры выглядят абсолютно естественно благодаря
              способности пропускать свет так же, как натуральная эмаль. В
              Dentico Clinic мы используем цифровые технологии для моделирования
              вашей будущей улыбки еще до начала лечения.
            </p>
          </div>
        </div>
      </section>

      {/* 3. PROCESS */}
      <section className="py-16 bg-gray-50" data-id="element-3061">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3062">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center" data-id="element-3063">
            Путь к новой улыбке
          </h2>
          <div className="grid md:grid-cols-4 gap-8" data-id="element-3064">
            {[{
            step: '01',
            title: 'Консультация',
            desc: 'Обсуждение пожеланий, фотопротокол и цифровое моделирование улыбки.'
          }, {
            step: '02',
            title: 'Планирование',
            desc: 'Примерка макета (mock-up), чтобы вы увидели результат до начала работ.'
          }, {
            step: '03',
            title: 'Подготовка',
            desc: 'Минимальная обработка зубов и снятие точных оттисков или сканирование.'
          }, {
            step: '04',
            title: 'Фиксация',
            desc: 'Установка готовых керамических виниров и финальная эстетика.'
          }].map((item, idx) => <Card key={idx} className="p-6 relative overflow-hidden" data-id="element-3065">
                <div className="text-6xl font-bold text-teal-50 absolute -top-4 -right-4 select-none" data-id="element-3066">
                  {item.step}
                </div>
                <div className="relative z-10" data-id="element-3067">
                  <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-3068">
                    {item.title}
                  </h3>
                  <p className="text-gray-600" data-id="element-3069">{item.desc}</p>
                </div>
              </Card>)}
          </div>
        </div>
      </section>

      {/* 4. TYPES */}
      <section className="py-16 bg-white" data-id="element-3070">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3071">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-3072">
            <div data-id="element-3073">
              <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-3074">
                Виды эстетической реставрации
              </h2>
              <div className="space-y-6" data-id="element-3075">
                <div className="flex gap-4" data-id="element-3076">
                  <div className="p-3 bg-teal-50 rounded-lg text-teal-600 h-fit" data-id="element-3077">
                    <Sparkles className="w-6 h-6" data-id="element-3078" />
                  </div>
                  <div data-id="element-3079">
                    <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-3080">
                      Керамические виниры
                    </h3>
                    <p className="text-gray-600" data-id="element-3081">
                      Изготавливаются в лаборатории из высокопрочной керамики.
                      Максимально эстетичные, долговечные и устойчивые к
                      изменению цвета.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4" data-id="element-3082">
                  <div className="p-3 bg-teal-50 rounded-lg text-teal-600 h-fit" data-id="element-3083">
                    <Palette className="w-6 h-6" data-id="element-3084" />
                  </div>
                  <div data-id="element-3085">
                    <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-3086">
                      Композитный бондинг
                    </h3>
                    <p className="text-gray-600" data-id="element-3087">
                      Художественная реставрация композитным материалом прямо в
                      кресле. Быстрее и доступнее, но требует периодической
                      полировки.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4" data-id="element-3088">
                  <div className="p-3 bg-teal-50 rounded-lg text-teal-600 h-fit" data-id="element-3089">
                    <Smile className="w-6 h-6" data-id="element-3090" />
                  </div>
                  <div data-id="element-3091">
                    <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-3092">
                      Smile Makeover
                    </h3>
                    <p className="text-gray-600" data-id="element-3093">
                      Комплексное преображение улыбки, которое может сочетать
                      виниры, коронки и отбеливание для идеального результата.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative h-[400px] rounded-2xl overflow-hidden shadow-xl" data-id="element-3094">
              <img src="https://images.unsplash.com/photo-1606811841689-23dfddce3e95?auto=format&fit=crop&w=1200&q=80" alt="Типы виниров" className="absolute inset-0 w-full h-full object-cover" data-id="element-3095" />
            </div>
          </div>
        </div>
      </section>

      {/* 5. BENEFITS */}
      <section className="py-16 bg-teal-50" data-id="element-3096">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3097">
          <div className="text-center mb-12" data-id="element-3098">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-3099">
              Почему стоит выбрать Dentico Clinic
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-3100">
            {[{
            title: 'Естественный вид',
            desc: 'Мы создаем улыбки, которые выглядят натурально, а не искусственно.'
          }, {
            title: 'Минимальное вмешательство',
            desc: 'Сохраняем максимум здоровых тканей зуба благодаря современным техникам.'
          }, {
            title: 'Русский язык',
            desc: 'Легкое общение и полное понимание ваших пожеланий.'
          }, {
            title: 'Удобная локация',
            desc: 'Клиника в центре Лондона (Angel), легко добраться из любого района.'
          }].map((item, idx) => <Card key={idx} className="p-6 bg-white text-center" data-id="element-3101">
                <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center text-teal-600 mb-4 mx-auto" data-id="element-3102">
                  <CheckCircle2 className="w-6 h-6" data-id="element-3103" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-3104">
                  {item.title}
                </h3>
                <p className="text-gray-600" data-id="element-3105">{item.desc}</p>
              </Card>)}
          </div>
        </div>
      </section>

      {/* 6. FAQ */}
      <section className="py-16 bg-white" data-id="element-3106">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3107">
          <div className="text-center mb-10" data-id="element-3108">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-3109">
              Частые вопросы о винирах
            </h2>
          </div>
          <div className="space-y-2" data-id="element-3110">
            {faqs.map((faq, idx) => <AccordionItem key={idx} question={faq.q} answer={faq.a} isOpen={openFaqIndex === idx} onToggle={() => toggleFaq(idx)} data-id="element-3111" />)}
          </div>
        </div>
      </section>

      {/* 7. INTERNAL LINKS */}
      <section className="py-12 bg-gray-50 border-t border-gray-100" data-id="element-3112">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3113">
          <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-3114">
            Смотрите также
          </h2>
          <div className="flex flex-wrap gap-4" data-id="element-3115">
            <Link to="/ru/russkij-stomatolog-london" data-id="element-3116">
              <Button variant="outline" size="sm" data-id="element-3117">
                Русскоязычный стоматолог
              </Button>
            </Link>
            <Link to="/ru/services/smile-makeover" data-id="element-3118">
              <Button variant="outline" size="sm" data-id="element-3119">
                Smile Makeover
              </Button>
            </Link>
            <Link to="/ru/services/whitening" data-id="element-3120">
              <Button variant="outline" size="sm" data-id="element-3121">
                Отбеливание
              </Button>
            </Link>
            <Link to="/ru/prices" data-id="element-3122">
              <Button variant="outline" size="sm" data-id="element-3123">
                Цены
              </Button>
            </Link>
            <Link to="/ru/contact" data-id="element-3124">
              <Button variant="outline" size="sm" data-id="element-3125">
                Контакты
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* 8. CTA */}
      <section className="py-16 bg-teal-900" data-id="element-3126">
        <div className="max-w-4xl mx-auto px-4 text-center" data-id="element-3127">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4" data-id="element-3128">
            Мечтаете об идеальной улыбке?
          </h2>
          <p className="text-xl text-teal-100 mb-8 max-w-2xl mx-auto" data-id="element-3129">
            Запишитесь на консультацию и узнайте, как виниры могут изменить вашу
            жизнь.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-3130">
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-3131">
              <Button size="lg" className="w-full gap-2 bg-white text-teal-900 hover:bg-teal-50" data-id="element-3132">
                <Calendar className="w-5 h-5" data-id="element-3133" />
                Записаться онлайн
              </Button>
            </a>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-3134">
              <Button variant="outline" size="lg" className="w-full gap-2 border-teal-400 text-teal-100 hover:bg-teal-800 hover:text-white" data-id="element-3135">
                <MessageCircle className="w-5 h-5" data-id="element-3136" />
                WhatsApp
              </Button>
            </a>
          </div>
        </div>
      </section>
    </Layout>;
}